import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import {
  AiChatActions,
  DocumentActions,
  selectChatIdByClientTag,
  selectCreatedChatIdByMysid,
  selectCurrentPerson,
  selectEventsByChatId,
  selectMainCompany,
  selectPersonalDocuments,
} from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import {
  AccountOutput,
  AiChatEvent,
  DocNode,
  DocumentType,
  PersonOutput,
  PUBLIC_ACCESS,
} from '@qtek/shared/models';
import {
  ALLOWED_DOCUMENT_ATTACHMENT_ACCEPT,
  ALLOWED_DOCUMENT_ATTACHMENT_FORMAT_ERROR_MESSAGE,
  isAllowedDocumentAttachment,
} from '@qtek/shared/utils';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { Author, ChatMsgVM } from '../../models/message.types';
import { AiChatEventSubscriptionService } from '../../services/ai-chat-event-subscription.service';
import { AiChatEnhancedMessageBubbleComponent } from '../messages';

interface PendingMessage {
  id: string;
  text: string;
  at: number;
  attachmentCount: number;
}

const PENDING_TIMESTAMP_TOLERANCE = 5000;

type SubscribeChatsPayload = Parameters<
  typeof AiChatActions.subscribeChats
>[0]['payload'];
type SubscribeChatEventsPayload = Parameters<
  typeof AiChatActions.subscribeChatEvents
>[0]['payload'];
type CreateChatPayload = Parameters<
  typeof AiChatActions.createChat
>[0]['payload'];
type SendMessagePayload = Parameters<
  typeof AiChatActions.sendMessage
>[0]['payload'];

@Component({
  selector: 'qt-ai-chat',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    TranslationCoreModule,
    AiChatEnhancedMessageBubbleComponent,
  ],
  templateUrl: './ai-chat.component.html',
  styleUrls: ['./ai-chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Accept attribute for the file input (allowed document + image types). */
  readonly allowedAttachmentAccept = ALLOWED_DOCUMENT_ATTACHMENT_ACCEPT;

  /** Inline error when user selects a disallowed file format (shown above input). */
  readonly attachmentFormatError = signal<string | null>(null);

  // Signal-based inputs
  readonly windowId = input.required<string>();
  readonly chatId = input<string | null>();
  readonly lazySubscribe = input(false);
  readonly typingLottieSrc = input<string | undefined>(
    'assets/animations/allo.lottie'
  );
  readonly typingLottieSize = input(48);

  @ViewChild('scroll', { static: true }) scroll!: ElementRef<HTMLElement>;
  @ViewChild('inputArea', { static: false })
  inputArea?: ElementRef<HTMLElement>;
  @ViewChild('filePicker', { static: false })
  filePicker?: ElementRef<HTMLInputElement>;
  @ViewChild('chatInput', { static: false })
  chatInput?: ElementRef<HTMLTextAreaElement>;

  /** Attached files Claude-style */
  readonly attachedFiles = signal<{ id: string; name: string; type: string }[]>(
    []
  );
  /** Pending uploads (name + type) shown until doc is resolved. */
  readonly pendingFilesForDisplay = signal<{ name: string; type: string }[]>(
    []
  );

  // Signals for reactive state management
  readonly draft = signal('');
  readonly attachedDocIds = signal<string[]>([]);
  readonly pendingUploads = signal(0);
  readonly isTyping = signal(false);
  readonly messages = signal<ChatMsgVM[]>([]);
  readonly pendingMessages = signal<ChatMsgVM[]>([]);
  readonly currentChatId = signal<string | null>(null);
  readonly isDropActive = signal(false);

  // Computed signals
  readonly allMessages = computed(() => [
    ...this.remoteMessages(),
    ...this.pendingMessages(),
  ]);

  readonly canAttach = computed(
    () => this.attachedDocIds().length + this.pendingUploads() < 8
  );

  readonly canSend = computed(() => {
    const draftText = this.draft().trim();
    const hasAttachments = this.attachedDocIds().length > 0;
    return (draftText || hasAttachments) && this.pendingUploads() === 0;
  });

  getPreviousMessageText(index: number): string {
    const list = this.allMessages();
    const prev = list[index - 1];
    return prev?.author === 'user' ? (prev.text ?? '') : '';
  }

  readonly typingDots = [0, 1, 2];
  readonly typingMessage: ChatMsgVM = {
    id: 'typing',
    text: '',
    author: 'bot',
    at: Date.now(),
  };

  // Private state
  private sendQueued = false;
  private queuedDraft: string | null = null;
  private pendingFileInfos = new Map<string, { type: string }>();
  private destroy$ = new Subject<void>();
  private chatId$ = new BehaviorSubject<string | null>(null);
  private typingSubject = new BehaviorSubject<boolean>(false);
  private lastPendingUserTs: number | null = null;
  private pendingMsgsSubject = new BehaviorSubject<ChatMsgVM[]>([]);
  private pendingQueue: PendingMessage[] = [];
  private hasSubscribedToChats = false;
  private dragCounter = 0;
  private activeChatSubscriptionId: string | null = null;
  private focusObserver: IntersectionObserver | null = null;
  private wasInputIntersecting = false;

  // Signal-based state
  private readonly resolvedChatId = signal<string | null>(null);
  private readonly remoteMessages = signal<ChatMsgVM[]>([]);

  // Signal-based injections
  private readonly store = inject(Store);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly chatEventSubscriptions = inject(
    AiChatEventSubscriptionService
  );

  constructor() {
    // Effect to sync typing state
    effect(() => {
      const typing = this.isTyping();
      this.typingSubject.next(typing);
    });

    // Effect to sync messages
    effect(() => {
      this.allMessages();
      untracked(() => this.scrollToBottomSoon());
    });

    // Effect to resolve chat ID from multiple sources
    effect(() => {
      const windowId = this.windowId();
      const chatIdFromInput = this.chatId();

      // Subscribe to store selectors and resolve chat ID
      const chatIdFromMysid$ = this.store.select(
        selectCreatedChatIdByMysid(`chats_${windowId}`)
      ) as Observable<string | null>;

      const chatIdFromTag$ = this.store.select(
        selectChatIdByClientTag(windowId)
      ) as Observable<string | null>;

      combineLatest([
        of(chatIdFromInput ?? null),
        chatIdFromMysid$,
        chatIdFromTag$,
      ])
        .pipe(
          map(
            ([fromInput, fromMysid, fromTag]) =>
              fromInput || fromMysid || fromTag || null
          ),
          distinctUntilChanged(),
          takeUntil(this.destroy$)
        )
        .subscribe((resolvedId: string | null) => {
          const prev = this.chatId$.value;
          if (prev !== resolvedId) {
            this.clearAllPending();
          }
          this.chatId$.next(resolvedId);
          this.currentChatId.set(resolvedId);
          this.resolvedChatId.set(resolvedId);
        });
    });

    // Effect to handle remote messages based on resolved chat ID
    effect(() => {
      const chatId = this.resolvedChatId();

      if (!chatId) {
        this.releaseChatEventSubscription();
        this.remoteMessages.set([]);
        return;
      }

      // Subscribe to chat events for this chat ID (ref-counted)
      this.ensureChatEventSubscription(chatId);

      // Subscribe to events for this chat
      (
        this.store.select(selectEventsByChatId(chatId)) as Observable<
          AiChatEvent[]
        >
      )
        .pipe(
          map(events => (events ?? []).map(event => this.toChatMessage(event))),
          takeUntil(this.destroy$)
        )
        .subscribe(messages => {
          const enhanced = messages.map(msg => ({ ...msg }));
          this.reconcilePending(enhanced);
          this.markResponseAttachments(enhanced);
          this.remoteMessages.set(enhanced);
          this.cdr.markForCheck();

          if (!enhanced.length) {
            if (!this.pendingQueue.length) {
              this.isTyping.set(false);
              this.lastPendingUserTs = null;
            } else {
              this.isTyping.set(true);
            }
            return;
          }

          if (this.lastPendingUserTs !== null) {
            for (let i = enhanced.length - 1; i >= 0; i--) {
              const candidate = enhanced[i];
              if (candidate.author === 'bot') {
                if (candidate.at >= this.lastPendingUserTs) {
                  this.isTyping.set(false);
                  this.lastPendingUserTs = null;
                }
                break;
              }
            }
          }
        });
    });
  }

  ngOnInit(): void {
    const chatIdValue = this.chatId();
    if (chatIdValue) this.chatId$.next(chatIdValue);

    if (!this.lazySubscribe()) {
      this.ensureChatSubscription();
    }

    this.setupDocWatcher();
  }

  ngAfterViewInit(): void {
    const el = this.chatInput?.nativeElement;
    if (!el) return;
    this.focusObserver = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry) return;
        const isVisible = entry.isIntersecting;
        if (isVisible && !this.wasInputIntersecting) {
          el.focus();
        }
        this.wasInputIntersecting = isVisible;
      },
      { threshold: 0.1 }
    );
    this.focusObserver.observe(el);
  }

  onChatAreaClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('a, button, [role="button"], input, textarea')) return;
    this.focusChatInput();
  }

  private focusChatInput(): void {
    this.chatInput?.nativeElement?.focus();
  }

  private setupDocWatcher(): void {
    this.store
      .select(selectPersonalDocuments)
      .pipe(takeUntil(this.destroy$))
      .subscribe((list: DocNode[] | null) => {
        const docs = list ?? [];
        const docsReversed = [...docs].reverse();
        let mutated = false;
        for (const doc of docsReversed) {
          const key = doc.fileName ?? doc.desc ?? doc.origfl;
          const id = doc._id;
          if (!key || !id) continue;

          let matchedPendingKey: string | null = key;
          let pending = this.pendingFileInfos.get(key);
          if (!pending && this.pendingFileInfos.size > 0) {
            const pendingNames = [...this.pendingFileInfos.keys()];
            const fallback = pendingNames.find(
              n =>
                key.endsWith(n) ||
                n.endsWith(key) ||
                key.includes(n) ||
                n.includes(key)
            );
            if (fallback) {
              pending = this.pendingFileInfos.get(fallback)!;
              matchedPendingKey = fallback;
            } else {
              matchedPendingKey = null;
            }
          }
          if (
            pending &&
            matchedPendingKey &&
            !this.attachedDocIds().includes(id)
          ) {
            this.attachedDocIds.update(ids => [...ids, id]);
            this.attachedFiles.update(files => [
              ...files,
              { id, name: key, type: pending!.type },
            ]);
            this.pendingFileInfos.delete(matchedPendingKey);
            this.pendingFilesForDisplay.update(list =>
              list.filter(f => f.name !== matchedPendingKey)
            );
            this.pendingUploads.update(count => Math.max(0, count - 1));
            mutated = true;
          }
        }
        if (mutated) {
          this.cdr.markForCheck();
        }
        this.checkAutoSendAfterUploads();
      });
  }

  ngOnDestroy(): void {
    this.focusObserver?.disconnect();
    this.focusObserver = null;
    this.store.dispatch(
      AiChatActions.unsubscribeChats({ mysid: `chats_${this.windowId()}` })
    );
    this.releaseChatEventSubscription();

    this.destroy$.next();
    this.destroy$.complete();
    this.typingSubject.complete();
    this.pendingMsgsSubject.complete();
  }

  openFilePicker(): void {
    this.filePicker?.nativeElement?.click();
  }

  private readonly chatInputMinHeight = 24;

  growChatInput(): void {
    const el = this.chatInput?.nativeElement;
    if (!el) return;
    const isEmpty = !el.value.trim();
    if (isEmpty) {
      el.style.height = `${this.chatInputMinHeight}px`;
      return;
    }
    el.style.height = '0';
    const newHeight = Math.max(
      this.chatInputMinHeight,
      Math.min(el.scrollHeight, 200)
    );
    el.style.height = `${newHeight}px`;
  }

  onChatKeydown(event: Event): void {
    const e = event as KeyboardEvent;
    if (e.key !== 'Enter') return;
    if (e.shiftKey) return;
    e.preventDefault();
    this.send();
  }

  fileTypeLabel(type: string): string {
    if (!type) return '';
    const mimeMap: Record<string, string> = {
      'application/msword': 'DOC',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        'DOCX',
      'application/vnd.ms-excel': 'XLS',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        'XLSX',
      'application/vnd.ms-powerpoint': 'PPT',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        'PPTX',
      'text/plain': 'TXT',
      'text/markdown': 'MD',
    };
    if (mimeMap[type]) return mimeMap[type];
    const part = type.split('/').pop() ?? type;
    return part.toUpperCase();
  }

  removeAttachedFile(docId: string): void {
    this.attachedDocIds.update(ids => ids.filter(id => id !== docId));
    this.attachedFiles.update(files => files.filter(f => f.id !== docId));
  }

  removePendingFile(fileName: string): void {
    this.pendingFileInfos.delete(fileName);
    this.pendingFilesForDisplay.update(list =>
      list.filter(f => f.name !== fileName)
    );
    this.pendingUploads.update(count => Math.max(0, count - 1));
    this.cdr.markForCheck();
  }

  onFilesSelected(evt: Event): void {
    const input = evt.target as HTMLInputElement;
    const files = input?.files ? Array.from(input.files) : [];
    this.uploadFiles(files, () => {
      if (input) input.value = '';
    });
  }

  onDragEnter(event: DragEvent): void {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter++;
    this.isDropActive.set(true);
  }

  onDragOver(event: DragEvent): void {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent): void {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    const container = this.inputArea?.nativeElement;
    const related = event.relatedTarget as Node | null;
    const stillInside = container && related && container.contains(related);
    if (!stillInside) {
      this.dragCounter = Math.max(0, this.dragCounter - 1);
      if (this.dragCounter === 0) {
        this.isDropActive.set(false);
      }
    }
  }

  onDrop(event: DragEvent): void {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    const files = Array.from(event.dataTransfer?.files ?? []);
    this.dragCounter = 0;
    this.isDropActive.set(false);
    setTimeout(() => {
      this.uploadFiles(files);
    }, 0);
  }

  private checkAutoSendAfterUploads(): void {
    if (this.pendingUploads() === 0 && this.sendQueued) {
      const text = (this.queuedDraft || '').trim();
      this.sendQueued = false;
      this.queuedDraft = null;
      if (text || this.attachedDocIds().length) {
        this.sendInternal(text).catch(console.error);
      }
    }
  }

  // Ensure we have a chatId - signal-based approach
  private async ensureChatId(): Promise<string> {
    this.ensureChatSubscription();

    const currentId = this.chatId$.value;
    if (currentId) return currentId;

    const mysid = `chats_${this.windowId()}`;
    const payload: CreateChatPayload = {
      res: {},
      s: 'model',
      view: 'pln',
    };
    this.store.dispatch(AiChatActions.createChat({ payload }));

    return new Promise<string>(resolve => {
      (
        this.store.select(selectCreatedChatIdByMysid(mysid)) as Observable<
          string | null
        >
      )
        .pipe(
          filter((x): x is string => !!x),
          take(1)
        )
        .subscribe(newId => {
          this.chatId$.next(newId);
          resolve(newId);
        });
    });
  }

  // Build and dispatch the chatEvent insert with documentIds - signal-based approach
  private async sendInternal(text: string): Promise<void> {
    const chatId = await this.ensureChatId();

    const event: AiChatEvent = {
      chatId,
      data: text,
      src: 'user',
      ts: Date.now(),
    };

    if (this.attachedDocIds().length) {
      event.documentIds = [...this.attachedDocIds()];
    }

    const payload: SendMessagePayload = {
      id: chatId,
      res: event,
      s: 'model',
      view: 'pln',
    };

    this.store.dispatch(AiChatActions.sendMessage({ payload }));
    const timestamp = event.ts ?? Date.now();
    this.lastPendingUserTs = timestamp;
    this.isTyping.set(true);
    this.addPendingMessage(text, timestamp);

    // reset input state
    this.draft.set('');
    this.attachedDocIds.set([]);
    this.attachedFiles.set([]);
    this.pendingFilesForDisplay.set([]);
    queueMicrotask(() => this.growChatInput());
    this.scrollToBottomSoon();
  }

  // send handler - signal-based approach
  send(): void {
    const text = this.draft()?.trim() ?? '';
    const docIds = this.attachedDocIds();
    const pending = this.pendingUploads();
    if (!text && !docIds.length) return;

    if (pending > 0 && docIds.length === 0) {
      this.sendQueued = true;
      this.queuedDraft = text;
      return;
    }
    this.attachmentFormatError.set(null);
    this.sendInternal(text).catch(console.error);
  }

  private scrollToBottomSoon() {
    queueMicrotask(() => {
      const el = this.scroll?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }

  private ensureChatSubscription(): void {
    if (this.hasSubscribedToChats) return;

    const mysid = `chats_${this.windowId()}`;
    const payload: SubscribeChatsPayload = {
      mysid,
      s: 'model',
      view: 'pln',
    };
    this.store.dispatch(AiChatActions.subscribeChats({ payload }));
    this.hasSubscribedToChats = true;
  }

  private toChatMessage(event: AiChatEvent): ChatMsgVM {
    const at = event.ts ?? Date.now();
    const author: Author = event.src === 'user' ? 'user' : 'bot';
    const text = event.data ?? '';
    const attachmentCount = event.documentIds?.length ?? 0;

    return {
      id: `${event.chatId}_${at}`,
      text,
      author,
      at,
      attachmentCount,
      chatId: event.chatId,
      ts: event.ts,
    };
  }

  private ensureChatEventSubscription(chatId: string): void {
    if (this.activeChatSubscriptionId === chatId) return;

    this.releaseChatEventSubscription();

    const mysid = `chatEvent_${chatId}`;
    const payload: SubscribeChatEventsPayload = {
      mysid,
      q: [{ k: 'chatId', op: 'EQ', v: [chatId] }],
      s: 'model',
      view: 'pln',
    };

    this.chatEventSubscriptions.acquire(chatId, payload);
    this.activeChatSubscriptionId = chatId;
  }

  private releaseChatEventSubscription(): void {
    if (!this.activeChatSubscriptionId) return;
    this.chatEventSubscriptions.release(this.activeChatSubscriptionId);
    this.activeChatSubscriptionId = null;
  }

  private addPendingMessage(text: string, at: number): void {
    const id = `pending_${at}_${Math.random().toString(16).slice(2)}`;
    const attachmentCount = this.attachedDocIds().length;
    const message: ChatMsgVM = {
      id,
      text,
      author: 'user',
      at,
      attachmentCount,
    };

    this.pendingQueue.push({
      id,
      text: text.trim(),
      at,
      attachmentCount,
    });
    this.pendingMessages.update(messages => [...messages, message]);
    this.cdr.markForCheck();
  }

  private reconcilePending(remote: ChatMsgVM[]): void {
    if (!this.pendingQueue.length) return;

    const remoteUser = remote.filter(msg => msg.author === 'user');
    if (!remoteUser.length) return;

    let pendingList = this.pendingMessages();
    let changed = false;

    this.pendingQueue = this.pendingQueue.filter(pending => {
      const match = remoteUser.find(msg => {
        const messageAt = msg.at ?? 0;
        const timeDiff = Math.abs(messageAt - pending.at);
        const withinWindow =
          messageAt >= pending.at || timeDiff <= PENDING_TIMESTAMP_TOLERANCE;
        return withinWindow && msg.text.trim() === pending.text.trim();
      });

      if (match) {
        if (
          pending.attachmentCount > 0 &&
          (!match.attachmentCount ||
            match.attachmentCount < pending.attachmentCount)
        ) {
          match.attachmentCount = pending.attachmentCount;
        }
        const idx = pendingList.findIndex(m => m.id === pending.id);
        if (idx !== -1) {
          pendingList = [
            ...pendingList.slice(0, idx),
            ...pendingList.slice(idx + 1),
          ];
          changed = true;
        }
        return false;
      }

      return true;
    });

    if (changed) {
      this.pendingMessages.set(pendingList);
      this.cdr.markForCheck();
    }
  }

  private uploadFiles(files: File[], done?: () => void): void {
    if (!files?.length) {
      done?.();
      return;
    }

    const allowed = files.filter(isAllowedDocumentAttachment);
    const rejectedCount = files.length - allowed.length;
    if (rejectedCount > 0) {
      this.attachmentFormatError.set(
        ALLOWED_DOCUMENT_ATTACHMENT_FORMAT_ERROR_MESSAGE
      );
    }
    const uploads = allowed.slice(0, 8);
    if (!uploads.length) {
      done?.();
      return;
    }
    this.attachmentFormatError.set(null);

    this.pendingUploads.update(count => count + uploads.length);
    const typeDefault = 'application/octet-stream';
    const newPendingDisplay: { name: string; type: string }[] = [];
    uploads.forEach(f => {
      const type = f.type || typeDefault;
      this.pendingFileInfos.set(f.name, { type });
      newPendingDisplay.push({ name: f.name, type });
    });
    this.pendingFilesForDisplay.update(list => [...list, ...newPendingDisplay]);

    this.store
      .select(selectCurrentPerson)
      .pipe(
        take(1),
        switchMap((prs: PersonOutput | null | undefined) =>
          prs?.prsId
            ? of(`prs=${prs.prsId}`)
            : this.store.select(selectMainCompany).pipe(
                take(1),
                map((acn: AccountOutput | null | undefined) =>
                  acn?._id ? `acn=${acn._id}` : null
                )
              )
        )
      )
      .subscribe((ks: string | null) => {
        uploads.forEach(file => {
          const fd = new FormData();
          fd.append('data', file);
          fd.append('_dcs', file.name);
          fd.append('_acc', PUBLIC_ACCESS.toString());
          if (ks) fd.append('_ks', ks);

          this.store.dispatch(
            DocumentActions.uploadDocuments({
              payload: { data: fd, attr: DocumentType.PERSONAL_DOCUMENTS },
            })
          );
        });

        done?.();
      });
  }

  private hasFiles(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types ?? []).includes('Files');
  }

  private markResponseAttachments(messages: ChatMsgVM[]): void {
    let carry = 0;
    for (const message of messages) {
      if (message.author === 'user') {
        carry = message.attachmentCount ?? 0;
        continue;
      }

      if (message.author === 'bot') {
        if (
          carry > 0 &&
          (!message.attachmentCount || message.attachmentCount < carry)
        ) {
          message.attachmentCount = carry;
        }
        carry = 0;
      }
    }
  }

  private clearAllPending(): void {
    if (!this.pendingQueue.length && !this.pendingMessages().length) {
      return;
    }
    this.pendingQueue = [];
    this.pendingMessages.set([]);
    this.cdr.markForCheck();
  }
}
